import { RecruitEmailAdmin } from './RecruitEmailAdmin';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Loader2, Mail, FileText, ChevronDown, Eye, RefreshCw } from 'lucide-react';
import { LwDrawer, LwDrawerHeader, LwAvatar } from '@/components/lw';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { PersonalInfoSection } from './PersonalInfoSection';
import { ProfessionalInfoSection } from './ProfessionalInfoSection';
import { StatusSection } from './StatusSection';
import { OnboardingTab } from './OnboardingTab';
import { ContractDetailsSection } from './ContractDetailsSection';
import { AmendmentsSection } from './AmendmentsSection';
import { SendInvitationModal } from './SendInvitationModal';
import { OnboardingEmailConfirmationModal } from './OnboardingEmailConfirmationModal';
import { CompanySignatureModal } from './CompanySignatureModal';
import { ContractReviewModal } from './ContractReviewModal';
import { VersionHistoryAccordion } from './VersionHistoryAccordion';
import { RecruiterSelect } from './RecruiterSelect';
import { RecruitmentProgressionBar } from './RecruitmentProgressionBar';
import { recruitmentFormSchema, type RecruitmentFormValues } from './validations';
import { buildRecruitmentFormValues, type RecruitmentSaveOutcome } from './formDefaults';
import type { RecruitmentCandidate } from './types';
import type { OnboardingTask } from './onboarding-types';
import { getRecruitLinkedState } from '@/services/workspaceService';
import { useContractInstance, useRecruitmentTemplate } from '@/hooks/useRecruitment';
import { updateContractInstance, createContractInstance } from '@/services/recruitmentService';
import { useAuth } from '@/contexts/useAuth';
import { createComponentLogger } from '@/utils/logger';
import { supabase } from '@/utils/supabase';
import {
  resolveRecruitEmailStage,
  type RecruitEmailDelivery,
  type RecruitOnboardingEmailType
} from './emailStage';

const EMAIL_DELIVERY_LABELS: Record<RecruitOnboardingEmailType, string> = {
  partial_onboarding: 'Partial onboarding',
  full_onboarding: 'Full onboarding',
  nmls_clearance: 'NMLS clearance'
};

function deliveryActor(delivery: RecruitEmailDelivery): string {
  if (delivery.requested_by_name) {
    return delivery.requested_by_name;
  }
  if (delivery.trigger_source === 'automatic') {
    return 'Automatic NMLS transition';
  }
  if (delivery.trigger_source === 'legacy_import') {
    return 'Legacy communication import';
  }
  return 'Administrator';
}

function deliveryTimestamp(delivery: RecruitEmailDelivery): string {
  const timestamp = delivery.sent_at || delivery.last_attempt_at || delivery.created_at;
  return new Date(timestamp).toLocaleString();
}

const log = createComponentLogger('RecruitmentDrawer', 'recruitment');

interface RecruitmentDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidate?: RecruitmentCandidate | null;
  /**
   * Persist the candidate. MUST be awaited and MUST report an explicit
   * outcome — the drawer only shows success and clears its dirty baseline on
   * `saved`. A parent that swallows failures leaves the form permanently
   * dirty while claiming the save worked.
   */
  onSave: (data: RecruitmentFormValues) => Promise<RecruitmentSaveOutcome> | RecruitmentSaveOutcome;
  initialTab?: string;
  autoOpenReviewModal?: boolean;
}

export const RecruitmentDrawer: React.FC<RecruitmentDrawerProps> = ({
  open,
  onOpenChange,
  candidate,
  onSave,
  initialTab,
  autoOpenReviewModal
}) => {
  const isEditMode = !!candidate;
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [activeTab, setActiveTab] = useState('lo-data');
  const [invitationModalOpen, setInvitationModalOpen] = useState(false);
  const [onboardingEmailModalOpen, setOnboardingEmailModalOpen] = useState(false);
  const [emailDeliveries, setEmailDeliveries] = useState<RecruitEmailDelivery[]>([]);
  const [linkedEncompassUserId, setLinkedEncompassUserId] = useState<string | null>(null);
  const [emailContextLoading, setEmailContextLoading] = useState(false);
  const [isSavingContract, setIsSavingContract] = useState(false);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);

  // Onboarding task state (initialized via useEffect below)
  const [onboardingTasks, setOnboardingTasks] = useState<OnboardingTask[]>([]);

  // Auth hook
  const { user } = useAuth();

  // Contract hooks (only in edit mode)
  const { template, loading: templateLoading } = useRecruitmentTemplate();
  const {
    instance: contractInstance,
    loading: instanceLoading,
    saveInstance,
    refetch: refetchInstance
  } = useContractInstance(candidate?.id);

  // Apply deep-link initialTab / autoOpenReviewModal once per open cycle.
  // Tab applies immediately; modal must wait for the contract instance to
  // resolve so we don't pop on a finalized agreement.
  const tabAppliedRef = useRef(false);
  const modalAppliedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      tabAppliedRef.current = false;
      modalAppliedRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (!open || !isEditMode) {
      return;
    }
    if (tabAppliedRef.current) {
      return;
    }
    if (initialTab) {
      setActiveTab(initialTab);
    }
    tabAppliedRef.current = true;
  }, [open, isEditMode, initialTab]);

  useEffect(() => {
    if (!open || !isEditMode) {
      return;
    }
    if (modalAppliedRef.current) {
      return;
    }
    if (!autoOpenReviewModal) {
      return;
    }
    if (instanceLoading) {
      return;
    }

    // Guard against showing a modal for the wrong candidate (instance from a
    // previous render may still be in scope while a new fetch is mid-flight).
    if (contractInstance && candidate && contractInstance.recruitId !== candidate.id) {
      return;
    }

    const inReviewableState =
      contractInstance?.hasLoSignature &&
      !contractInstance?.hasRecruiterSignature &&
      contractInstance?.status !== 'voided';

    if (inReviewableState) {
      setReviewModalOpen(true);
    }
    modalAppliedRef.current = true;
  }, [open, isEditMode, autoOpenReviewModal, instanceLoading, contractInstance, candidate]);

  const form = useForm<RecruitmentFormValues>({
    resolver: zodResolver(recruitmentFormSchema),
    defaultValues: buildRecruitmentFormValues(candidate, user?.id)
  });

  const { handleSubmit, reset, formState: { isDirty } } = form;

  const refreshEmailContext = useCallback(async () => {
    if (!open || !candidate?.id) {
      setEmailDeliveries([]);
      setLinkedEncompassUserId(null);
      return;
    }

    setEmailContextLoading(true);
    try {
      const deliveriesPromise = supabase
        .from('recruit_email_deliveries')
        .select('id, email_type, status, recipient_email, trigger_source, requested_by, attempt_count, provider_message_id, error_code, error_message, created_at, last_attempt_at, sent_at')
        .eq('recruit_id', candidate.id)
        .order('created_at', { ascending: false });
      // Linked-user state comes from the authorized edge function: a direct
      // users query is RLS-hidden from non-admin Talent Pipeline users, which
      // would blank Task 4 and the email button after a reload.
      const encompassPromise = candidate.migratedToUserId
        ? getRecruitLinkedState(candidate.id)
        : Promise.resolve(null);

      const [deliveriesResult, linkedState] = await Promise.all([
        deliveriesPromise,
        encompassPromise
      ]);
      if (deliveriesResult.error) {
        throw deliveriesResult.error;
      }
      if (linkedState?.error) {
        log.warn('Unable to resolve linked user state', {
          metadata: { candidateId: candidate.id, error: linkedState.error }
        });
      }

      const deliveries = (deliveriesResult.data || []) as RecruitEmailDelivery[];
      const requestedByIds = [...new Set(deliveries.flatMap((delivery) =>
        delivery.requested_by ? [delivery.requested_by] : []
      ))];
      const requestedByNames = new Map<string, string>();
      if (requestedByIds.length > 0) {
        const requesterResult = await supabase
          .from('users')
          .select('id, full_name, email')
          .in('id', requestedByIds);
        if (requesterResult.error) {
          log.warn('Unable to resolve onboarding email requesters', {
            metadata: { candidateId: candidate.id }
          });
        } else {
          for (const requester of requesterResult.data || []) {
            requestedByNames.set(
              requester.id,
              requester.full_name?.trim() || requester.email || 'Administrator'
            );
          }
        }
      }

      setEmailDeliveries(deliveries.map((delivery) => ({
        ...delivery,
        requested_by_name: delivery.requested_by
          ? requestedByNames.get(delivery.requested_by) || null
          : null
      })));
      setLinkedEncompassUserId(linkedState?.encompassUserId || null);
    } catch (error) {
      log.error('Failed to load recruit email delivery context', {
        metadata: { candidateId: candidate.id }
      }, error as Error);
      setEmailDeliveries([]);
    } finally {
      setEmailContextLoading(false);
    }
  }, [candidate?.id, candidate?.migratedToUserId, open]);

  useEffect(() => {
    void refreshEmailContext();
  }, [refreshEmailContext]);

  // Initialize onboarding tasks from database
  useEffect(() => {
    const initializeOnboardingTasks = async () => {
      if (!candidate) {
        // Reset to default pending tasks for new candidates
        setOnboardingTasks([
          {
            id: '1',
            title: 'Link User Account',
            description: 'Connect recruit to user account for Lendwise platform access',
            category: 'Technology',
            status: 'pending',
            order: 1,
            fields: [],
            fieldValues: {}
          },
          {
            id: '2',
            title: 'Gmail Account Assigned',
            description: 'Assign company Gmail account with @lendwisemtg.com domain',
            category: 'Technology',
            status: 'pending',
            order: 2,
            fields: [
              {
                id: 'gmail_account',
                label: 'Gmail Account',
                type: 'email',
                required: true,
                placeholder: 'firstname.lastname@lendwisemtg.com',
                validation: {
                  pattern: '^[a-zA-Z0-9._%+-]+@lendwisemtg\\.com$',
                  message: 'Must be a valid @lendwisemtg.com email address'
                }
              },
              {
                id: 'temporary_password',
                label: 'Temporary Password',
                type: 'password',
                required: true,
                placeholder: 'Enter temporary password'
              }
            ],
            fieldValues: {}
          },
          {
            id: '3',
            title: 'Optimal Account Assigned',
            description: 'Assign Optimal Blue username. User will receive email from Optimal Blue to set up their own password.',
            category: 'Technology',
            status: 'pending',
            order: 3,
            fields: [
              {
                id: 'optimal_username',
                label: 'Optimal Blue Username',
                type: 'text',
                required: true,
                placeholder: 'Enter Optimal Blue username'
              }
            ],
            fieldValues: {}
          },
          {
            id: '4',
            title: 'Create Encompass Account',
            description: 'Generate LOS user credentials for loan processing',
            category: 'Technology',
            status: 'pending',
            order: 4,
            fields: [],
            fieldValues: {}
          }
        ]);
        return;
      }

      // Check Task 1: Link User Account status - based on data existence
      const task1Complete = !!candidate.migratedToUserId;

      // Check Task 2: Gmail Account status - based on data existence
      const task2Complete = !!(candidate.gmailAccount && candidate.gmailAccount.trim() !== '');

      // Check Task 3: Optimal Username status - based on data existence
      const task3Complete = !!(candidate.optimalUsername && candidate.optimalUsername.trim() !== '');

      // Check Task 4: Encompass Account status. The Encompass ID alone is not
      // proof of a finished task for post-feature recruits: a provisioned
      // account also needs its saved temp password (a failed save must keep
      // the task open so the recovery action stays reachable), while
      // existing-provenance access needs no password. Legacy rows (no
      // onboarding_access_mode) keep the historical ID-only rule.
      let task4Complete = false;
      let linkedUser: { encompass_user_id?: string | null } | null = null;
      if (candidate.migratedToUserId) {
        try {
          // Authorized read: direct users queries are RLS-hidden from
          // non-admin Talent Pipeline users
          const linkedState = await getRecruitLinkedState(candidate.id);
          if (linkedState.error) {
            throw new Error(linkedState.error);
          }
          linkedUser = { encompass_user_id: linkedState.encompassUserId };
          const passwordSatisfied = !candidate.onboardingAccessMode
            || !!candidate.encompassTempPassword
            || candidate.encompassAccessMode === 'existing';
          task4Complete = !!linkedState.encompassUserId && passwordSatisfied;
        } catch (error) {
          log.error('Failed to check Encompass status for linked user', {
            recruitId: candidate.id,
            linkedUserId: candidate.migratedToUserId
          }, error as Error);
        }
      }

      // Check Task 5: NMLS Authorization status - based on date existence
      const task5Complete = !!candidate.nmlsAuthorizationDate;

      // Check Task 6: Xactus Credentials status - based on request date existence
      const task6Complete = !!candidate.xactusRequestDate;

      // Initialize tasks with database values
      setOnboardingTasks([
        {
          id: '1',
          title: 'Gmail Account Assigned',
          description: 'Assign company Gmail account with @lendwisemtg.com domain',
          category: 'Technology',
          status: task2Complete ? 'completed' : 'pending',
          order: 1,
          fields: [
            {
              id: 'gmail_account',
              label: 'Gmail Account',
              type: 'email',
              required: true,
              placeholder: 'firstname.lastname@lendwisemtg.com',
              validation: {
                pattern: '^[a-zA-Z0-9._%+-]+@lendwisemtg\\.com$',
                message: 'Must be a valid @lendwisemtg.com email address'
              }
            },
            {
              id: 'temporary_password',
              label: 'Temporary Password',
              type: 'password',
              required: true,
              placeholder: 'Enter temporary password'
            }
          ],
          fieldValues: candidate.gmailAccount
            ? {
                gmail_account: candidate.gmailAccount,
                temporary_password: candidate.gmailTempPassword || '',
                access_mode: candidate.onboardingAccessMode || ''
              }
            : {}
        },
        {
          id: '2',
          title: 'Link User Account',
          description: 'Connect recruit to user account for Lendwise platform access',
          category: 'Technology',
          status: task1Complete ? 'completed' : 'pending',
          order: 2,
          fields: [],
          fieldValues: task1Complete && candidate.migratedToUserId
            ? {
                linked_user_id: candidate.migratedToUserId,
                linked_user_email: '' // Will be populated by component
              }
            : {}
        },
        {
          id: '3',
          title: 'Optimal Account Assigned',
          description: 'Assign Optimal Blue username. User will receive email from Optimal Blue to set up their own password.',
          category: 'Technology',
          status: task3Complete ? 'completed' : 'pending',
          order: 3,
          fields: [
            {
              id: 'optimal_username',
              label: 'Optimal Blue Username',
              type: 'text',
              required: true,
              placeholder: 'Enter Optimal Blue username'
            }
          ],
          fieldValues: candidate.optimalUsername
            ? { optimal_username: candidate.optimalUsername }
            : {}
        },
        {
          id: '4',
          title: 'Create Encompass Account',
          description: 'Generate LOS user credentials for loan processing',
          category: 'Technology',
          status: task4Complete ? 'completed' : 'pending',
          order: 4,
          fields: [],
          fieldValues: task4Complete && linkedUser && linkedUser.encompass_user_id
            ? {
                encompass_username: linkedUser.encompass_user_id,
                encompass_password: candidate.encompassTempPassword || '',
                encompass_access_mode: candidate.encompassAccessMode || ''
              }
            : {
                // Provenance travels with the pending task so the recovery
                // action can tell "we created it and lost the password" apart
                // from a normal first-time creation.
                encompass_access_mode: candidate.encompassAccessMode || ''
              }
        },
        {
          id: '5',
          title: 'NMLS Granted Authorization to Conduct Business',
          description: 'Record the date NMLS granted authorization for this loan officer to conduct business',
          category: 'Compliance',
          status: task5Complete ? 'completed' : 'pending',
          order: 5,
          fields: [], // Custom UI in OnboardingTab - no automatic field rendering
          fieldValues: candidate.nmlsAuthorizationDate
            ? { nmls_authorization_date: candidate.nmlsAuthorizationDate }
            : {}
        },
        {
          id: '6',
          title: 'Request Xactus Credentials',
          description: 'Submit billing credentials request to Xactus verification services',
          category: 'Credentials',
          status: task6Complete ? 'completed' : (task2Complete ? 'pending' : 'locked'),
          order: 6,
          fields: [],
          dependencies: ['1'], // Requires Gmail from Task 1
          fieldValues: task6Complete
            ? {
                request_date: candidate.xactusRequestDate || '',
                request_status: candidate.xactusRequestStatus || 'pending'
              }
            : {}
        },
        {
          id: '7',
          title: 'Identification Documents',
          description: 'Upload and verify two forms of valid government-issued identification',
          category: 'Compliance',
          status: candidate?.idPrimaryFrontPath && candidate?.idSecondaryFrontPath && candidate?.idValidatedAt
            ? 'completed'
            : (candidate?.idPrimaryFrontPath && candidate?.idSecondaryFrontPath ? 'pending' : 'pending'),
          order: 7,
          fields: [],
          dependencies: [], // No dependencies - can start immediately
          fieldValues: {
            primaryIdType: candidate?.idPrimaryType || '',
            secondaryIdType: candidate?.idSecondaryType || '',
            validated: candidate?.idValidatedAt ? 'true' : 'false'
          }
        }
      ]);
    };

    initializeOnboardingTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- granular candidate.* deps are intentional; full `candidate` would re-fire on unrelated field changes
  }, [candidate?.id, candidate?.migratedToUserId, candidate?.step2Completed, candidate?.step3Completed, candidate?.gmailAccount, candidate?.optimalUsername, candidate?.encompassTempPassword, candidate?.encompassAccessMode, candidate?.nmlsAuthorizationDate, candidate?.xactusRequestDate, candidate?.idPrimaryFrontPath, candidate?.idSecondaryFrontPath, candidate?.idValidatedAt]);

  // Establish the form baseline from the candidate.
  //
  // Keyed on open + candidate id (NOT the candidate object identity) for two
  // reasons. Reopening the same candidate previously left the form on whatever
  // `handleClose`'s bare `reset()` had wiped it to, because the object
  // reference had not changed and the effect never re-ran — that produced a
  // blank required `recruiterId` and made every later submit fail validation
  // silently. And keying on identity meant a background refetch mid-edit
  // clobbered the user's in-progress changes.
  const initializedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      initializedKeyRef.current = null;
      return;
    }
    const key = `${candidate?.id ?? 'new'}:${user?.id ?? ''}`;
    if (initializedKeyRef.current === key) {
      return;
    }
    initializedKeyRef.current = key;
    reset(buildRecruitmentFormValues(candidate, user?.id));
  }, [open, candidate, reset, user?.id]);

  const onSubmit = async (data: RecruitmentFormValues) => {
    // Guard: Don't submit if form hasn't changed (prevents phantom submissions)
    if (!isDirty && isEditMode) {
      log.debug('Form submission skipped - no changes detected', {
        candidateId: candidate?.id,
        isDirty,
        isEditMode
      });
      return;
    }

    setIsSubmitting(true);

    try {
      log.debug('Form data submitted', {
        mode: isEditMode ? 'edit' : 'create',
        position: data.position,
        isDirty
      });

      // Await the real persistence. Previously this fired a 1s setTimeout and
      // called onSave without awaiting, so success was announced before the
      // database round-trip finished — and regardless of whether it succeeded.
      // onSave is required: a missing-callback fallback of `{ status: 'saved' }`
      // would reintroduce exactly the false success this contract exists to stop.
      const outcome: RecruitmentSaveOutcome = await onSave(data);

      if (outcome.status === 'blocked') {
        // Not an error, but not a save either (e.g. the Talent Pipeline
        // duplicate guard). Never show success and never clear dirty.
        toast.warning('Candidate not saved', { description: outcome.message });
        return;
      }

      if (outcome.status === 'failed') {
        toast.error('Failed to save candidate', { description: outcome.message });
        return;
      }

      // Only a confirmed save establishes the new clean baseline, and the
      // PERSISTED record wins when the parent returns one. reset() sets the
      // current values and the defaults from the same object, so a
      // server-normalised value is displayed and still compares clean.
      //
      // It has to come from the server rather than from `data`: the
      // initialisation effect deliberately ignores same-id `candidate`
      // refreshes so a background refetch cannot clobber an in-progress edit,
      // which leaves this the only route a refreshed record has into the form.
      // Resetting to the submitted values would hide database normalisation
      // and display values that were never persisted.
      reset(buildRecruitmentFormValues(outcome.candidate, user?.id), {
        keepDefaultValues: false
      });

      toast.success(
        isEditMode
          ? 'Candidate updated successfully'
          : 'Candidate added to recruitment pipeline',
        {
          description: `${data.firstName} ${data.lastName} - ${data.status}`
        }
      );

      // Drawer stays open after save - user can continue editing or close manually
    } catch (error) {
      log.error('Save error', { mode: isEditMode ? 'edit' : 'create' }, new Error('Recruit save failed'));
      toast.error('Failed to save candidate', {
        description: error instanceof Error ? error.message : 'Please try again or contact support'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Surface validation failures. `handleSubmit` silently does nothing when the
   * schema rejects, and `recruiterId` is required but has no visible error —
   * so a blank recruiter made "Update Candidate" a no-op with zero feedback.
   */
  const onInvalid = (errors: Record<string, unknown>) => {
    const fields = Object.keys(errors);
    log.warn('Form submission blocked by validation', { metadata: { candidateId: candidate?.id, fields } });
    toast.error('Please fix the highlighted fields', {
      description: fields.includes('recruiterId')
        ? 'An assigned recruiter is required.'
        : `Check: ${fields.join(', ')}`
    });
  };

  const handleSaveContract = async () => {
    if (!candidate?.id || !template) {
      log.error('Cannot save contract: missing required data', {
        candidateId: candidate?.id,
        hasTemplate: !!template,
        templateLoading,
        templateId: template?.id
      });
      toast.error(
        !candidate?.id
          ? 'Cannot save contract: missing candidate'
          : 'Cannot save contract: template not loaded yet',
        {
          description: templateLoading ? 'Please wait for template to load' : 'Please try again'
        }
      );
      return;
    }

    setIsSavingContract(true);

    try {
      const formData = form.getValues();

      // ContractVariableData models an absent years-of-experience as
      // `undefined`; the form schema now yields `null` for a blank field
      // (blank must persist as NULL, not 0). Normalise at the boundary rather
      // than weakening either type.
      const contractData = {
        ...formData,
        yearsOfExperience: formData.yearsOfExperience ?? undefined
      };

      // Save contract instance with current form data
      const success = await saveInstance(
        contractData,
        template.id,
        template.version,
        template.sections
      );

      if (success) {
        // Refetch to get latest instance
        await refetchInstance();

        toast.success('Contract saved successfully', {
          description: contractInstance ? 'Contract version updated' : 'Contract finalized'
        });
      }
    } catch (error) {
      log.error('Failed to save contract', { error, candidateId: candidate?.id });
      toast.error('Failed to save contract', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsSavingContract(false);
    }
  };

  const handleClose = () => {
    if (isDirty) {
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to close?'
      );
      if (!confirmed) {
return;
}
    }
    onOpenChange(false);
    // Reset to THIS candidate's values, not the blank defaults captured at
    // first mount. A bare reset() wiped required fields (notably recruiterId,
    // which is `''` when `useAuth` had not resolved at mount) and those blanks
    // survived a reopen of the same candidate.
    reset(buildRecruitmentFormValues(candidate, user?.id));
  };

  /**
   * Regenerate a new contract after voiding
   * Creates a fresh draft contract with content from the voided version
   */
  const handleRegenerateContract = async () => {
    if (!candidate?.id || !user?.id || !contractInstance) {
      toast.error('Unable to regenerate contract', {
        description: 'Missing required data'
      });
      return;
    }

    try {
      setIsSavingContract(true);

      // Get the latest template version
      const templateVersion = template?.version ?? 1;

      // Create new contract instance with fresh state
      await createContractInstance(
        {
          organization_id: user.organization_id!,
          recruit_id: candidate.id,
          template_id: template?.id,
          template_version: templateVersion,
          sections: contractInstance.sections || template?.sections || [],
          variable_data: contractInstance.variableData || {},
          checkbox_states: {}, // Fresh checkboxes
          version_number: (contractInstance.versionNumber || 1) + 1,
          parent_instance_id: contractInstance.id,
          is_active: true,
          status: 'draft'
        },
        user.id
      );

      // Refetch to get the new contract
      await refetchInstance();

      toast.success('Contract regenerated successfully', {
        description: 'A new draft contract has been created'
      });
    } catch (error) {
      log.error('Failed to regenerate contract', { error, candidateId: candidate.id });
      toast.error('Failed to regenerate contract', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsSavingContract(false);
    }
  };

  const taskValue = (taskId: string, fieldId: string) =>
    onboardingTasks.find((task) => task.id === taskId)?.fieldValues?.[fieldId];
  const emailStage = resolveRecruitEmailStage({
    status: candidate?.status || 'Inquiry',
    personalEmail: candidate?.email || '',
    companyEmail: taskValue('1', 'gmail_account') || candidate?.gmailAccount,
    gmailTempPassword: taskValue('1', 'temporary_password') || candidate?.gmailTempPassword,
    linkedUserId: taskValue('2', 'linked_user_id') || candidate?.migratedToUserId,
    optimalUsername: taskValue('3', 'optimal_username') || candidate?.optimalUsername,
    encompassUserId: taskValue('4', 'encompass_username') || linkedEncompassUserId,
    encompassTempPassword: taskValue('4', 'encompass_password') || candidate?.encompassTempPassword,
    accessMode: taskValue('1', 'access_mode') || candidate?.onboardingAccessMode,
    encompassAccessMode: taskValue('4', 'encompass_access_mode') || candidate?.encompassAccessMode,
    nmlsAuthorizationDate: taskValue('5', 'nmls_authorization_date') || candidate?.nmlsAuthorizationDate,
    deliveries: emailDeliveries
  });
  const latestEmailDeliveries = emailDeliveries.filter((delivery, index, deliveries) =>
    deliveries.findIndex((candidateDelivery) =>
      candidateDelivery.email_type === delivery.email_type
    ) === index
  );
  const emailActionLoading = emailContextLoading && candidate?.status === 'Approved';

  // Calculate onboarding stats from actual tasks
  const completedTasks = onboardingTasks.filter(t => t.status === 'completed').length;
  const totalTasks = onboardingTasks.length;
  const onboardingStats = {
    percentComplete: totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0
  };

  if (!open) {
    return null;
  }

  return (
    <LwDrawer onClose={handleClose} label={isEditMode ? 'Edit candidate' : 'Add loan officer'}>
      <LwDrawerHeader
        avatar={isEditMode && candidate ? <LwAvatar first={candidate.firstName} last={candidate.lastName} /> : undefined}
        name={isEditMode && candidate ? `${candidate.firstName} ${candidate.lastName}` : 'Add loan officer'}
        sub={
          isEditMode && candidate
            ? `NMLS #${candidate.nmlsNumber}${candidate.position ? ` · ${candidate.position}` : ''}`
            : 'New candidate for the recruiting pipeline'
        }
        onClose={handleClose}
      />

      <div className="dw-tabbar">
        <button className={`dw-toptab${activeTab === 'lo-data' ? ' active' : ''}`} onClick={() => setActiveTab('lo-data')} type="button">
          LO Data
        </button>
        <button className={`dw-toptab${activeTab === 'contract-details' ? ' active' : ''}`} onClick={() => setActiveTab('contract-details')} type="button">
          Contract Details
        </button>
        <button className={`dw-toptab${activeTab === 'onboarding' ? ' active' : ''}`} onClick={() => setActiveTab('onboarding')} type="button">
          Onboarding
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="dw-form">
        <div className="dw-body">
          {isEditMode && candidate && (
            <RecruitmentProgressionBar
              candidateId={candidate.id}
              contractInstance={contractInstance}
              onboardingStats={onboardingStats}
            />
          )}

          <RecruiterSelect
            value={form.watch('recruiterId')}
            onChange={(value) => form.setValue('recruiterId', value, { shouldDirty: true })}
            disabled={isSubmitting}
            autoAssigned={!isEditMode || (isEditMode && candidate?.recruiterId === candidate?.createdBy)}
          />

          {activeTab === 'lo-data' && (
            <>
              <PersonalInfoSection form={form} savedEmail={isEditMode} />
              {candidate?.id && <RecruitEmailAdmin key={candidate.id} recruitId={candidate.id} email={form.watch('email')} onChanged={email => form.resetField('email', { defaultValue: email })} />}
              <ProfessionalInfoSection form={form} />
              <StatusSection form={form} />
            </>
          )}

          {activeTab === 'contract-details' && (
            <>
              {contractInstance?.hasLoSignature &&
              contractInstance?.hasRecruiterSignature &&
              contractInstance?.status === 'signed' ? (
                <AmendmentsSection candidateId={candidate?.id} contractInstance={contractInstance} />
              ) : (
                <ContractDetailsSection form={form} candidateId={candidate?.id} />
              )}

              {candidate?.id && (
                <VersionHistoryAccordion
                  recruitId={candidate.id}
                  candidateName={`${candidate.firstName} ${candidate.lastName}`}
                />
              )}
            </>
          )}

          {activeTab === 'onboarding' && (
            <>
              {isEditMode && candidate && (
                <div className="mb-4 rounded-lg border bg-muted/30 p-4" aria-live="polite">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold">Onboarding email status</p>
                    <span className="text-xs font-medium text-muted-foreground">
                      {taskValue('5', 'nmls_authorization_date') || candidate.nmlsAuthorizationDate
                        ? 'NMLS authorized'
                        : 'NMLS authorization pending'}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {emailContextLoading ? 'Loading delivery history…' : emailStage.description}
                  </p>
                  {!emailContextLoading && latestEmailDeliveries.length > 0 && (
                    <ul className="mt-3 space-y-1 border-t pt-3 text-xs text-muted-foreground">
                      {latestEmailDeliveries.map((delivery) => (
                        <li key={delivery.id} className="flex flex-wrap justify-between gap-x-3 gap-y-1">
                          <span>
                            <span className="font-medium text-foreground">
                              {EMAIL_DELIVERY_LABELS[delivery.email_type]}
                            </span>
                            {' · '}{delivery.status}
                          </span>
                          <span>
                            {deliveryTimestamp(delivery)} · {deliveryActor(delivery)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              <OnboardingTab
                candidateId={candidate?.id}
                tasks={onboardingTasks}
                onTasksChange={setOnboardingTasks}
                onEmailDeliveryChange={refreshEmailContext}
              />
            </>
          )}
        </div>

        <div className="dw-foot">
          <span className="meta">{isDirty ? 'Unsaved changes' : ''}</span>
          <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting || isSavingContract}>
            Cancel
          </Button>

          {isEditMode && candidate && (
            <>
              {contractInstance?.hasLoSignature &&
              contractInstance?.hasRecruiterSignature &&
              contractInstance?.status === 'signed' ? (
                <Button
                  type="button"
                  variant="default"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setReviewModalOpen(true);
                  }}
                  disabled={isSubmitting || isSavingContract}
                >
                  <FileText className="mr-2 h-4 w-4" />
                  View Contract
                </Button>
              ) : contractInstance?.hasLoSignature &&
                !contractInstance?.hasRecruiterSignature &&
                contractInstance?.status !== 'voided' ? (
                <Button
                  type="button"
                  variant="default"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setReviewModalOpen(true);
                  }}
                  disabled={isSubmitting || isSavingContract}
                >
                  <FileText className="mr-2 h-4 w-4" />
                  Review Contract
                </Button>
              ) : contractInstance?.status === 'voided' ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button type="button" variant="outline" disabled={isSubmitting || isSavingContract} className="flex gap-1">
                      <FileText className="h-4 w-4" />
                      Contract Options
                      <ChevronDown className="h-4 w-4 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="lw-ds">
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.preventDefault();
                        setReviewModalOpen(true);
                      }}
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      Review Voided Contract
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.preventDefault();
                        handleRegenerateContract();
                      }}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Regenerate Contract
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSaveContract}
                  disabled={isSubmitting || isSavingContract || templateLoading}
                >
                  {isSavingContract ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving Contract...
                    </>
                  ) : (
                    <>
                      <FileText className="mr-2 h-4 w-4" />
                      Save Contract
                    </>
                  )}
                </Button>
              )}

              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (emailStage.action === 'initial_invitation') {
                    setInvitationModalOpen(true);
                  } else {
                    setOnboardingEmailModalOpen(true);
                  }
                }}
                disabled={
                  isSubmitting
                    || isSavingContract
                    || emailActionLoading
                    || emailStage.action === 'none'
                    || (emailStage.action === 'initial_invitation' && !contractInstance)
                }
                title={
                  emailStage.action === 'initial_invitation' && !contractInstance
                    ? 'Save contract first'
                    : emailStage.description
                }
              >
                {emailActionLoading || emailStage.state === 'processing' ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Mail className="mr-2 h-4 w-4" />
                )}
                {emailActionLoading ? 'Loading Email Status…' : emailStage.buttonText}
              </Button>
            </>
          )}

          <Button type="submit" disabled={isSubmitting || isSavingContract}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>{isEditMode ? 'Update Candidate' : 'Add Candidate'}</>
            )}
          </Button>
        </div>
      </form>

        {/* Send Invitation Modal */}
        {isEditMode && candidate && (
          <SendInvitationModal
            open={invitationModalOpen}
            onOpenChange={setInvitationModalOpen}
            candidate={candidate}
            onSendSuccess={async (invitationId) => {
              // Update contract status to 'sent'
              if (contractInstance && user?.id) {
                try {
                  await updateContractInstance(
                    contractInstance.id,
                    {
                      status: 'sent',
                      sent_at: new Date().toISOString()
                    },
                    user.id
                  );
                  // Refetch to update UI
                  await refetchInstance();
                } catch (error) {
                  log.error('Error updating contract status', { instanceId: contractInstance.id }, error as Error);
                }
              }

              toast.success('Invitation sent successfully', {
                description: `Invitation ID: ${invitationId}`
              });
            }}
          />
        )}

        {isEditMode && candidate && emailStage.emailType && (
          <OnboardingEmailConfirmationModal
            open={onboardingEmailModalOpen}
            onOpenChange={setOnboardingEmailModalOpen}
            candidate={candidate}
            stage={emailStage}
            onSendSuccess={refreshEmailContext}
          />
        )}

        {/* Company Signature Modal */}
        {isEditMode && candidate && (
          <CompanySignatureModal
            open={signatureModalOpen}
            onOpenChange={setSignatureModalOpen}
            candidate={candidate}
            contractInstance={contractInstance}
            onSignSuccess={async () => {
              // Refetch instance to show updated signature data
              await refetchInstance();
              toast.success('Contract signed successfully');
            }}
          />
        )}

        {/* Contract Review Modal (for reviewing before counter-signing) */}
        {isEditMode && candidate && (
          <ContractReviewModal
            open={reviewModalOpen}
            onOpenChange={setReviewModalOpen}
            candidate={candidate}
            contractInstance={contractInstance}
            loading={instanceLoading}
            onRefresh={async () => {
              // Refetch instance after void or sign action
              await refetchInstance();
            }}
          />
        )}
    </LwDrawer>
  );
};
